import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type {
  GetLegacyServicePickersResponse,
  GetPickersResponse,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { applyDealsToPickerGroup } from '@backmarket/nuxt-layer-deals/applyDealsToPickerGroup'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { applyOperationOnPickers } from './applyOperationOnPickers'
import { applyUpgradeForLessTagGate } from './applyUpgradeForLessTagGate'
import { getMobilePlanStep } from './steps/mobilePlan'
import { getTradeInAUStep, getTradeInStep } from './steps/tradeIn'
import type { Step } from './types'
import { mapStep } from './utils'

/**
 * This function is here to map the response of existing endpoints (pickers, best_offers) to
 * something easily usable by the Customization Funnel.
 *
 * We chose to reuse existing endpoints over a new dedicated one to gain some time developing the
 * MVP that will be A/B tested on smartphones only. This is not meant to be scaled to any other category
 * without a new endpoint to be created.
 */
export const createCustomizationFunnelData = (
  i18n: ReturnType<typeof useI18n>,
  pickers: GetPickersResponse,
  pickersServices: GetLegacyServicePickersResponse['pickerGroups'],
  product: GetProductResponse,
  price: Price | null | undefined,
  discountedPrice: Price,
  experiments: Record<`experiment.${string}`, string>,
): Step[] => {
  const variantsSteps: Step[] = pickers.pickerGroups
    .map((group) => {
      let processedGroup = group

      if (experiments['experiment.flashSalesOnPDP'] === 'enabled') {
        processedGroup = applyDealsToPickerGroup(group, i18n)
      }

      return mapStep(product, processedGroup)
    })
    // TS way of narrowing the type of the returned Array
    // to discard nullish values, while filtering out all
    // falsy values
    .filter((step): step is Step => Boolean(step))

  const servicesSteps: Step[] = pickersServices
    .map((group) => {
      switch (group.id) {
        case 'mobile_plan':
          return getMobilePlanStep(i18n, group, price)
        case 'trade_in': {
          // Declare both the telco picker placement and the step component identity
          // in funnel data, keeping the template free of hard-coded step id checks.
          return {
            ...getTradeInStep(product, group, i18n, discountedPrice),
            showTelcoPickerBefore: true as const,
            isTradeInStep: true as const,
          }
        }
        default:
          return null
      }
    })
    // TS way of narrowing the type of the returned Array
    // to discard nullish values, while filtering out all
    // falsy values
    .filter((step): step is Step => Boolean(step))

  let steps: Step[] = [...variantsSteps, ...servicesSteps]

  if (experiments['experiment.tradeInAU'] === 'enabled') {
    const tradeInAUStep: Step = getTradeInAUStep(i18n)
    steps.push(tradeInAUStep)
  }

  // All operations to be applied on pickers
  // We iterate over service pickers to retrieve the operations associated to the selected ones.
  const operations = servicesSteps.map((step) => {
    const operation = step.options.find(({ selected }) => !!selected)
      ?.operation || { type: 'NoOperation' as const, price: null }

    return {
      id: step?.id,
      operation,
    }
  })

  operations.forEach(({ id, operation }) => {
    steps = applyOperationOnPickers(steps, id, operation)
  })

  return steps.map((step) => ({
    ...step,
    options: applyUpgradeForLessTagGate(step.options, step.id, experiments),
  }))
}
